import { ParseUtil } from '@reown/appkit-common';
import { mainnet } from '@reown/appkit/networks';
import { NAMESPACE_EVENTS, NAMESPACE_METHODS } from '../Constants';
import { extractEvmAddresses } from '../utils/EvmUtils';
export class NamespaceController {
    appKit;
    accountController;
    constructor(appKit, accountController) {
        this.appKit = appKit;
        this.accountController = accountController;
    }
    async buildSessionApprovalResponse(proposal) {
        const accountStatus = await this.accountController?.getAccountStatus();
        if (accountStatus === 'disconnected') {
            throw new Error('User is disconnected');
        }
        const account = this.accountController?.getAccount();
        const namespaces = {};
        let sessionProperties;
        // EIP155 (EVM)
        const { proposalChains: eip155Chains, proposalMethods: eip155Methods } = this.extractProposalData(proposal, 'eip155');
        if (eip155Chains.length !== 0) {
            const { namespace: eip155Namespace, sessionProperties: evmSessionProperties } = await this.buildEvmNamespace(eip155Chains, eip155Methods, account);
            namespaces.eip155 = eip155Namespace;
            sessionProperties = evmSessionProperties;
        }
        // Solana
        const { proposalChains: solanaChains, proposalMethods: solanaMethods } = this.extractProposalData(proposal, 'solana');
        if (solanaChains.length !== 0) {
            const { namespace: solanaNamespace } = this.buildSolanaNamespace(solanaChains, solanaMethods, account);
            namespaces.solana = solanaNamespace;
        }
        return {
            namespaces,
            sessionProperties
        };
    }
    async buildEvmNamespace(proposalChains, proposalMethods, account) {
        if (!extractEvmAddresses(account).saAddress) {
            await this.appKit?.switchNetwork(mainnet); // switch to mainnet to capture smart account address
            account = this.accountController?.getAccount();
        }
        const { saAddress, eoaAddress } = extractEvmAddresses(account);
        const approvedChains = this.getOverlappingChains(proposalChains);
        const smartAccounts = approvedChains.map(chain => `${chain}:${saAddress}`);
        const eoaAccounts = approvedChains.map(chain => `${chain}:${eoaAddress}`);
        const activeChainId = 'eip155:1';
        const preferredAccount = account?.eip155?.embeddedWalletInfo?.accountType === 'smartAccount'
            ? smartAccounts[0]
            : eoaAccounts[0];
        const { address: preferredAddress } = ParseUtil.parseCaipAddress(preferredAccount);
        const preferredAccountForActiveChain = `${activeChainId}:${preferredAddress}`;
        const allAccounts = [
            preferredAccountForActiveChain,
            ...[...smartAccounts, ...eoaAccounts].filter(account => account !== preferredAccountForActiveChain)
        ];
        const namespace = this.buildNamespaceObject('eip155', approvedChains, allAccounts, proposalMethods);
        const userInfo = account?.eip155?.embeddedWalletInfo?.user;
        const authProvider = account?.eip155?.embeddedWalletInfo?.authProvider;
        const sessionProperties = {
            smartAccounts: JSON.stringify(smartAccounts),
            ...(userInfo?.email && { email: userInfo.email }),
            ...(userInfo?.username && { username: userInfo.username }),
            ...(authProvider && { provider: authProvider })
        };
        return {
            namespace,
            sessionProperties
        };
    }
    buildSolanaNamespace(proposalChains, proposalMethods, account) {
        const caipAddress = account?.solana?.caipAddress;
        if (!caipAddress) {
            throw new Error('No solana account found');
        }
        const namespace = this.buildNamespaceObject('solana', proposalChains, [caipAddress], proposalMethods);
        return {
            namespace
        };
    }
    getOverlappingChains(proposalChains) {
        const appKitChains = this.appKit?.options.networks.map(network => typeof network.id === 'number' ? `eip155:${network.id}` : network.id) || [];
        if (!proposalChains) {
            return appKitChains;
        }
        const overlappingChains = proposalChains.filter(chain => appKitChains.includes(chain));
        return overlappingChains;
    }
    buildNamespaceObject(namespace, chains, accountIds, proposalMethods) {
        const webWalletMethods = NAMESPACE_METHODS[namespace];
        const methods = proposalMethods
            ? proposalMethods.filter(method => webWalletMethods.includes(method))
            : webWalletMethods;
        const result = {
            chains,
            methods,
            events: NAMESPACE_EVENTS[namespace],
            accounts: accountIds
        };
        return result;
    }
    extractProposalData(proposal, namespace) {
        const optionalChains = proposal.params.optionalNamespaces?.[namespace]?.chains || [];
        const requiredChains = proposal.params.requiredNamespaces?.[namespace]?.chains || [];
        const proposalChains = [...new Set([...optionalChains, ...requiredChains])];
        const optionalMethods = proposal.params.optionalNamespaces?.[namespace]?.methods || [];
        const requiredMethods = proposal.params.requiredNamespaces?.[namespace]?.methods || [];
        const proposalMethods = [...new Set([...optionalMethods, ...requiredMethods])];
        return { proposalChains, proposalMethods };
    }
}
//# sourceMappingURL=NamespaceController.js.map